import { ReactNode, useMemo } from 'react'

import { CLIENT_SELF_PAY_STATUS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { SummaryDetailProps } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/summaryDetail/SummaryDetail'
import type {
  SponsoredBenefitsSidePanelData,
  SponsoredBenefitsSidePanelVariant,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/clientDetailsSidePanel/sponsoredBenefits/SponsoredBenefitsSidePanelSection'

import { HPISelfPayStatus } from './useHPISelfPayStatus'
import { healthPlanStates } from '../../../../../common/constants/appConstants'

type UseGetSponsoredBenefitsSidePanelDataArgs = {
  clientDetails: ClientObject | undefined
  clientCustomer: CustomerInfo | undefined
  selectedEpisode: Episode | undefined
  benefitsSummaryDetails: SummaryDetailProps[] | undefined
  sessionCountDetails: SummaryDetailProps[] | undefined
  bhbSessionCount: SummaryDetailProps | null | undefined
  highRiskSessionDetailsLoaded: boolean
  hpiSelfPay: HPISelfPayStatus
  selfPayContinuationOfCareStatus: ReactNode
  healthPlanStatus: ReactNode
  sessionsRemaining: number
  sessionLimitCategory: string | undefined
  hasEligibilityError: boolean | undefined
  isZeroDollarCostShare: boolean | undefined
  isLyraSelect: boolean
  benefitsResetDate: string
  isDAEngageProvider: boolean
}

export const useGetSponsoredBenefitsSidePanelData = ({
  clientDetails,
  clientCustomer,
  selectedEpisode,
  benefitsSummaryDetails,
  sessionCountDetails,
  bhbSessionCount,
  highRiskSessionDetailsLoaded,
  hpiSelfPay,
  selfPayContinuationOfCareStatus,
  healthPlanStatus,
  sessionsRemaining,
  sessionLimitCategory,
  hasEligibilityError,
  isZeroDollarCostShare,
  isLyraSelect,
  benefitsResetDate,
  isDAEngageProvider,
}: UseGetSponsoredBenefitsSidePanelDataArgs): SponsoredBenefitsSidePanelData | undefined => {
  const { isSelfPayEnabled } = useFlags()

  const isEligibilityCheckRequired = Boolean(
    clientDetails?.country === 'US' &&
    clientCustomer?.eap_eligibility_check_enabled === 'true' &&
    selectedEpisode?.program_name,
  )
  const isEligibilityReady = !isEligibilityCheckRequired || hasEligibilityError !== undefined

  return useMemo(() => {
    if (
      !clientDetails ||
      !selectedEpisode ||
      !clientCustomer ||
      !isEligibilityReady ||
      sessionCountDetails === undefined ||
      bhbSessionCount === undefined ||
      !highRiskSessionDetailsLoaded
    ) {
      return undefined
    }

    const details = (benefitsSummaryDetails ?? []).filter((detail) => !detail.customComponent)
    const hasEapCoverage = sessionCountDetails.length > 0
    const hasHpiCoverage = bhbSessionCount !== null
    const supportsSelfPay = Boolean(
      isSelfPayEnabled &&
      clientDetails.customer_support_self_pay &&
      Programs.getProgramConfigByName(selectedEpisode.program_name)?.selfPayEligible,
    )
    const selfPayStatusMap: Partial<Record<string, 'active' | 'eligible'>> = {
      [CLIENT_SELF_PAY_STATUS.ACTIVE]: 'active',
      [CLIENT_SELF_PAY_STATUS.ELIGIBLE]: 'eligible',
    }
    const selfPayStatus =
      supportsSelfPay && clientDetails.self_pay_status ? selfPayStatusMap[clientDetails.self_pay_status] : undefined
    const usesAnnualVisitDisplay = Boolean(
      clientCustomer.display_visits_per_issue_per_year || clientCustomer.display_visits_per_issue_per_year_by_program,
    )
    const isEapNearSessionLimit = hasEapCoverage && !usesAnnualVisitDisplay && sessionsRemaining <= 3
    const hasHpiOnlyCoverage = hasHpiCoverage && !hasEapCoverage
    const hasNoCoverage = details.length === 0
    const isHpiSelfPayActive = Boolean(
      hasHpiOnlyCoverage &&
      hpiSelfPay.isActive &&
      selfPayStatus === 'active' &&
      (clientDetails.health_plan_status === healthPlanStates.NOT_READY ||
        clientDetails.health_plan_status === healthPlanStates.NOT_ELIGIBLE),
    )
    const isHealthPlanReady = Boolean(
      hasHpiOnlyCoverage &&
      (clientDetails.health_plan_status === healthPlanStates.READY ||
        (clientDetails.health_plan_status === healthPlanStates.HEALTH_PLAN_PAYMENT_MISSING && isZeroDollarCostShare)),
    )

    let variant: SponsoredBenefitsSidePanelVariant = 'active'
    if (hasEligibilityError) {
      variant = 'eligibilityNotConfirmed'
    } else if (
      selfPayStatus === 'active' &&
      ((isEapNearSessionLimit && sessionsRemaining <= 1) || isHpiSelfPayActive || hasNoCoverage)
    ) {
      variant = 'selfPayActive'
    } else if (isHealthPlanReady) {
      variant = 'healthPlan'
    } else if (isEapNearSessionLimit && sessionsRemaining <= 0) {
      variant = 'outOfSessions'
    } else if (isEapNearSessionLimit) {
      variant = 'runningLow'
    } else if (hasNoCoverage && !selfPayStatus) {
      variant = 'noCoverage'
    }

    return {
      sponsorName: clientDetails.displayed_employer || '-',
      clientFirstName: clientDetails.preferred_first_name || clientDetails.first_name || '',
      benefitsResetDate,
      details,
      variant,
      sessionLimitCategory,
      selfPayStatus,
      clientPronouns: clientDetails.pronouns,
      additionalInfo: hpiSelfPay.isActive
        ? hpiSelfPay.inlineMessage
        : selfPayContinuationOfCareStatus || healthPlanStatus,
      warningBanner: hpiSelfPay.banner,
      isLyraSelect,
      eligibilityAssistance: isDAEngageProvider ? 'helpCenter' : 'careNavigationTeam',
    }
  }, [
    clientDetails,
    selectedEpisode,
    clientCustomer,
    isEligibilityReady,
    sessionCountDetails,
    bhbSessionCount,
    highRiskSessionDetailsLoaded,
    benefitsSummaryDetails,
    isSelfPayEnabled,
    sessionsRemaining,
    sessionLimitCategory,
    hpiSelfPay,
    hasEligibilityError,
    isZeroDollarCostShare,
    selfPayContinuationOfCareStatus,
    healthPlanStatus,
    benefitsResetDate,
    isLyraSelect,
    isDAEngageProvider,
  ])
}
